/**
 * Runtime configuration contract — the `/config.json` document every deployed
 * dashboard reads before it mounts (DP-4750 / DSCO-5635).
 *
 * The document is produced by DevOps per tenant from encore-infra values and
 * served next to `index.html`. This module is the FE side of that contract:
 * the shape, and a validator that fails closed. It is pure (no fetch, no DOM)
 * so it can be unit-tested; `config.ts` does the loading.
 *
 * Every value here is public by design (OAuth public client id, hostnames,
 * browser-side ingest keys). A value that is not public cannot enter this
 * document — see the admission rule in the HLD.
 */

export const SUPPORTED_SCHEMA_VERSION = 1;

export type UrlShape = 'service-first' | 'client-first';

export interface RuntimeAuthConfig {
  domain: string;
  clientId: string;
  audience: string;
  /** Optional: the dashboard also resolves it per tenant at login time. */
  organization?: string;
}

export interface RuntimeObservabilityConfig {
  groundcoverRumDsn?: string;
  groundcoverRumIngestionKey?: string;
  bugsnagApiKey?: string;
  pendoApiKey?: string;
  /** OTLP HTTP traces endpoint for the OpenTelemetry exporter (e.g. https://<tenant>.platform.grcv.io/v1/traces). */
  otlpEndpoint?: string;
  /** Exporter API key (Groundcover ingestion key) — distinct from the RUM ingestion key. */
  groundcoverIngestionKey?: string;
}

export interface RuntimeConfig {
  schemaVersion: number;
  /** Stack name, e.g. `ikea-devo`. Replaces the build-time `VITE_APP_ENV`. */
  stack: string;
  /** Tenant the document was rendered for. Cross-checked against the host. */
  tenant?: string;
  /** Base domain without the tenant, e.g. `api.ikea-devo.locus-dev.com`. */
  apiBaseDomain: string;
  /** Base domain of the per-tenant apps-studio edge, without the tenant. */
  customAppBaseDomain: string;
  /**
   * Where the tenant sits in generated hosts.
   *   - 'service-first' (default): api.{tenant}.{apiBaseDomain}
   *   - 'client-first': {tenant}.{apiBaseDomain}
   */
  urlShape: UrlShape;
  auth: RuntimeAuthConfig;
  auth0CustomClaimsNamespace: string;
  observability: RuntimeObservabilityConfig;
  maps: { googleMapsApiKey?: string };
  /**
   * Optional per-MFE manifest URL overrides ({ app: manifestUrl }). A local-dev
   * and pinning escape hatch; absent in production documents.
   */
  customAppRemotes: Record<string, string>;
}

export class RuntimeConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuntimeConfigError';
  }
}

/** Suffix of the local-dev hostnames (`<tenant>-local.dashboard.<stack>…`). */
const LOCAL_HOST_SUFFIX = '-local';

/** Host label that marks the dashboard app in a tenant hostname. */
const DASHBOARD_LABEL = 'dashboard';

/** The tenant label of `dashboard.<tenant>.…` or `<tenant>.dashboard.…`. */
function tenantSegment(parts: readonly string[]): string | undefined {
  if (parts[0] === DASHBOARD_LABEL) {
    return parts[1];
  }
  if (parts[1] === DASHBOARD_LABEL) {
    return parts[0];
  }
  return undefined;
}
/** Shape used when a document omits `urlShape`. */
const DEFAULT_URL_SHAPE: UrlShape = 'service-first';
const URL_SHAPES: readonly UrlShape[] = [DEFAULT_URL_SHAPE, 'client-first'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireString(
  record: Record<string, unknown>,
  key: string,
  path: string
): string {
  const value = record[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new RuntimeConfigError(
      `"${path}" is required and must be a non-empty string`
    );
  }
  return value.trim();
}

function optionalString(
  record: Record<string, unknown>,
  key: string,
  path: string
): string | undefined {
  const value = record[key];
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value !== 'string') {
    throw new RuntimeConfigError(`"${path}" must be a string when present`);
  }
  return value.trim() === '' ? undefined : value.trim();
}

function optionalRecord(
  record: Record<string, unknown>,
  key: string
): Record<string, unknown> {
  const value = record[key];
  if (value === undefined || value === null) {
    return {};
  }
  if (!isRecord(value)) {
    throw new RuntimeConfigError(`"${key}" must be an object when present`);
  }
  return value;
}

/** Exact hosts a `customAppRemotes` URL may point at (local dev). */
const LOCAL_REMOTE_HOSTS: readonly string[] = ['localhost', '127.0.0.1'];
/** Host suffixes a `customAppRemotes` URL may point at (Amplify previews, dev stacks). */
const DEV_REMOTE_SUFFIXES: readonly string[] = [
  '.amplifyapp.com',
  '.locus-dev.com',
];

/**
 * Origins a custom-app manifest may be loaded from. `customAppRemotes` hands
 * URLs to the module-federation loader, which executes whatever it fetches,
 * so the field is dev/preview-only and every URL must point at one of:
 *  - a local dev server (localhost / 127.0.0.1),
 *  - an Amplify preview build (*.amplifyapp.com),
 *  - a dev host (*.locus-dev.com),
 *  - the document's own customAppBaseDomain or a subdomain of it.
 * Production documents must not carry the field at all (the encore-infra
 * schema rejects it); this check is the FE's own guard.
 */
function isAllowedRemoteOrigin(
  url: string,
  customAppBaseDomain: string
): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return false;
  }
  const host = parsed.hostname.toLowerCase();
  const base = customAppBaseDomain.toLowerCase();
  if (LOCAL_REMOTE_HOSTS.includes(host) || host === base) {
    return true;
  }
  const suffixes = [...DEV_REMOTE_SUFFIXES, `.${base}`];
  return suffixes.some(suffix => host.endsWith(suffix));
}

function readCustomAppRemotes(
  record: Record<string, unknown>,
  customAppBaseDomain: string
): Record<string, string> {
  const remotes = readStringMap(record, 'customAppRemotes');
  for (const [app, url] of Object.entries(remotes)) {
    if (!isAllowedRemoteOrigin(url, customAppBaseDomain)) {
      throw new RuntimeConfigError(
        `"customAppRemotes.${app}" points at "${url}", which is not a local, Amplify, locus-dev or ${customAppBaseDomain} origin`
      );
    }
  }
  return remotes;
}

function readStringMap(
  record: Record<string, unknown>,
  key: string
): Record<string, string> {
  const value = optionalRecord(record, key);
  const out: Record<string, string> = {};
  for (const [app, url] of Object.entries(value)) {
    if (!app || typeof url !== 'string' || url.trim() === '') {
      throw new RuntimeConfigError(
        `"${key}" must map app names to non-empty URL strings (bad entry: "${app}")`
      );
    }
    out[app] = url.trim();
  }
  return out;
}

/**
 * Tenant label carried by a deployed dashboard hostname, or null when the host
 * carries none (localhost, legacy hosts, path-based tenants).
 *   service-first: dashboard.{tenant}.{stack}.{zone}
 *   client-first:  {tenant}.dashboard.{stack}.{zone}
 * `stripLocalSuffix` removes a trailing "-local" (local-dev host convention);
 * callers pass `import.meta.env.DEV` so a deployment never strips anything.
 * Mirrors `extractClientIdFromSubdomain` in hooks/useClientId.ts without
 * importing it: config.ts must stay a leaf of the module graph so test suites
 * that mock the hooks module cannot break config loading.
 */
export function hostTenantFromHostname(
  hostname: string | null | undefined,
  options: { stripLocalSuffix: boolean }
): string | null {
  if (!hostname || hostname === 'localhost') {
    return null;
  }
  const parts = hostname.toLowerCase().split('.');
  if (parts.length < 4) {
    return null;
  }
  let tenant = tenantSegment(parts);
  if (!tenant) {
    return null;
  }
  if (options.stripLocalSuffix && tenant.endsWith(LOCAL_HOST_SUFFIX)) {
    tenant = tenant.slice(0, -LOCAL_HOST_SUFFIX.length);
  }
  return /^[a-z0-9_-]{1,64}$/.test(tenant) ? tenant : null;
}

export interface ValidateOptions {
  /**
   * Tenant derived from the browser hostname, when the host carries one. A
   * document rendered for another tenant is rejected: a wrong stack is worse
   * than a blank page.
   */
  hostTenant?: string | null;
}

/**
 * Validates an untrusted `/config.json` body. Throws `RuntimeConfigError` on
 * any defect; never returns a partial document.
 */
export function validateRuntimeConfig(
  input: unknown,
  options: ValidateOptions = {}
): RuntimeConfig {
  if (!isRecord(input)) {
    throw new RuntimeConfigError('document is not a JSON object');
  }

  const schemaVersion = input.schemaVersion;
  if (schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
    throw new RuntimeConfigError(
      `unsupported schemaVersion ${JSON.stringify(schemaVersion)} (this build understands ${SUPPORTED_SCHEMA_VERSION})`
    );
  }

  const stack = requireString(input, 'stack', 'stack');
  const tenant = optionalString(input, 'tenant', 'tenant')?.toLowerCase();
  const apiBaseDomain = requireString(input, 'apiBaseDomain', 'apiBaseDomain');
  const customAppBaseDomain = requireString(
    input,
    'customAppBaseDomain',
    'customAppBaseDomain'
  );

  const rawShape = optionalString(input, 'urlShape', 'urlShape');
  if (rawShape !== undefined && !URL_SHAPES.includes(rawShape as UrlShape)) {
    throw new RuntimeConfigError(
      `"urlShape" must be one of ${URL_SHAPES.join(', ')} (got "${rawShape}")`
    );
  }
  const urlShape: UrlShape =
    (rawShape as UrlShape | undefined) ?? DEFAULT_URL_SHAPE;

  if (!isRecord(input.auth)) {
    throw new RuntimeConfigError('"auth" is required and must be an object');
  }
  const auth: RuntimeAuthConfig = {
    domain: requireString(input.auth, 'domain', 'auth.domain'),
    clientId: requireString(input.auth, 'clientId', 'auth.clientId'),
    audience: requireString(input.auth, 'audience', 'auth.audience'),
    organization: optionalString(
      input.auth,
      'organization',
      'auth.organization'
    ),
  };

  const hostTenant = options.hostTenant?.toLowerCase();
  if (tenant && hostTenant && tenant !== hostTenant) {
    throw new RuntimeConfigError(
      `document was rendered for tenant "${tenant}" but this host serves "${hostTenant}"`
    );
  }

  const observabilityRaw = optionalRecord(input, 'observability');
  const mapsRaw = optionalRecord(input, 'maps');

  return {
    schemaVersion: SUPPORTED_SCHEMA_VERSION,
    stack,
    tenant,
    apiBaseDomain,
    customAppBaseDomain,
    urlShape,
    auth,
    // Required (DevOps review on DP-4750): with a default, an omitted field
    // boots the app with a wrong namespace and auth0Identity then reads
    // undefined for clientId / homeTenant / roleId without a throw.
    auth0CustomClaimsNamespace: requireString(
      input,
      'auth0CustomClaimsNamespace',
      'auth0CustomClaimsNamespace'
    ),
    observability: {
      groundcoverRumDsn: optionalString(
        observabilityRaw,
        'groundcoverRumDsn',
        'observability.groundcoverRumDsn'
      ),
      groundcoverRumIngestionKey: optionalString(
        observabilityRaw,
        'groundcoverRumIngestionKey',
        'observability.groundcoverRumIngestionKey'
      ),
      bugsnagApiKey: optionalString(
        observabilityRaw,
        'bugsnagApiKey',
        'observability.bugsnagApiKey'
      ),
      pendoApiKey: optionalString(
        observabilityRaw,
        'pendoApiKey',
        'observability.pendoApiKey'
      ),
      otlpEndpoint: optionalString(
        observabilityRaw,
        'otlpEndpoint',
        'observability.otlpEndpoint'
      ),
      groundcoverIngestionKey: optionalString(
        observabilityRaw,
        'groundcoverIngestionKey',
        'observability.groundcoverIngestionKey'
      ),
    },
    maps: {
      googleMapsApiKey: optionalString(
        mapsRaw,
        'googleMapsApiKey',
        'maps.googleMapsApiKey'
      ),
    },
    customAppRemotes: readCustomAppRemotes(input, customAppBaseDomain),
  };
}
